import { useId, useState } from 'react';
import { Button, InlineError } from '../../components/ui';
import { useCgAccess, useCgOrderCosts, useSetOrderCosts } from './hooks';
import {
  costNameSuggestions,
  orderProfit,
  parseBillDraft,
  type BillDraftLine,
  type CgOrderCost,
} from './orderProfit';
import type { CgOrder, CgOrderItem } from './types';
import { cgErrorMessage } from './types';
import './supplierBill.css';

/**
 * The supplier's bill for one order, and what the order made once it is in.
 *
 * Kyu, 22 September: "click on a box or a field and then we can just add the
 * amount there and then it auto calculates after that. I'm looking for
 * something simple." So: one button, a row per charge (a name and an amount),
 * Save. The name field offers every name typed on an earlier bill, so the
 * same charge is spelt the same way everywhere and the report can add it up.
 */

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function usd(value: number): string {
  return money.format(value);
}

function blankLine(): BillDraftLine {
  return { label: '', amount: '' };
}

function draftFrom(lines: readonly CgOrderCost[]): BillDraftLine[] {
  const rows = lines.map((line) => ({
    label: line.label,
    amount: Number(line.amount_usd).toFixed(2),
  }));
  return [...rows, blankLine()];
}

export function SupplierBill({
  order,
  items,
  canEdit,
}: {
  order: CgOrder;
  items: CgOrderItem[];
  canEdit: boolean;
}) {
  const costs = useCgOrderCosts();
  const save = useSetOrderCosts();
  const access = useCgAccess();
  const listId = useId();
  const [draft, setDraft] = useState<BillDraftLine[] | null>(null);
  const [error, setError] = useState('');

  // The module gate alone is not enough: cg_order_costs' RLS also requires
  // is_cg_member() (20270162000100, 20260921000100:120), which canEdit does
  // not check. Someone with cg_orders access who is not on the cg_members
  // list would see the button and have the save refused by the database.
  // While access is still loading, stay hidden rather than flash on and off.
  const canWrite = canEdit && access.data === true && !access.isPending;

  const all = costs.data ?? [];
  const lines = all
    .filter((line) => line.order_id === order.id)
    .sort((a, b) => a.position - b.position);
  const result = orderProfit(order, items, lines);

  function patch(index: number, change: Partial<BillDraftLine>) {
    setDraft((rows) => {
      if (rows === null) {
        return rows;
      }
      const next = rows.map((row, at) => (at === index ? { ...row, ...change } : row));
      // Always one empty row at the bottom, so adding a line is just typing.
      const last = next[next.length - 1];
      if (last !== undefined && (last.label !== '' || last.amount !== '')) {
        next.push(blankLine());
      }
      return next;
    });
  }

  function remove(index: number) {
    setDraft((rows) => {
      if (rows === null) {
        return rows;
      }
      const next = rows.filter((_, at) => at !== index);
      return next.length === 0 ? [blankLine()] : next;
    });
  }

  function submit() {
    if (draft === null) {
      return;
    }
    const parsed = parseBillDraft(draft);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError('');
    save.mutate(
      { orderId: order.id, lines: parsed.lines },
      {
        onSuccess: () => setDraft(null),
        onError: (cause) => setError(cgErrorMessage(cause.message)),
      },
    );
  }

  if (costs.isPending) {
    return <p className="order-page-muted">Loading the bill…</p>;
  }

  if (draft !== null) {
    const parsed = parseBillDraft(draft);
    const running = parsed.ok ? parsed.lines.reduce((sum, line) => sum + line.amount_usd, 0) : null;
    return (
      <div className="supplier-bill">
        <datalist id={listId}>
          {costNameSuggestions(all).map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <div className="supplier-bill-rows">
          {draft.map((row, index) => (
            <div className="supplier-bill-row" key={index}>
              <input
                className="ui-input"
                aria-label={`Cost name, line ${String(index + 1)}`}
                placeholder="Cost name, e.g. Bracelets"
                list={listId}
                maxLength={80}
                value={row.label}
                onChange={(event) => patch(index, { label: event.target.value })}
              />
              <input
                className="ui-input supplier-bill-amount"
                aria-label={`Amount, line ${String(index + 1)}`}
                placeholder="$0.00"
                inputMode="decimal"
                value={row.amount}
                onChange={(event) => patch(index, { amount: event.target.value })}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove line ${String(index + 1)}`}
                onClick={() => remove(index)}
              >
                ✕
              </Button>
            </div>
          ))}
        </div>
        <dl className="supplier-bill-sum">
          <dt>Bill total</dt>
          <dd className="supplier-bill-strong">{running === null ? '—' : usd(running)}</dd>
        </dl>
        {error === '' ? null : <InlineError>{error}</InlineError>}
        <div className="supplier-bill-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setDraft(null);
              setError('');
            }}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="supplier-bill">
      {lines.length === 0 ? (
        <p className="order-page-muted">
          {result.source === 'legacy'
            ? 'No bill lines yet. The cost below was typed in the order before bills existed.'
            : 'No bill yet. Add it when the supplier bills us.'}
        </p>
      ) : (
        <table className="supplier-bill-lines">
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <td>{line.label}</td>
                <td className="supplier-bill-num">{usd(Number(line.amount_usd))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <dl className="supplier-bill-sum">
        <dt>Revenue (order + S&amp;H)</dt>
        <dd>{usd(result.revenue)}</dd>
        {result.bill === null ? (
          <>
            <dt>Profit</dt>
            <dd className="supplier-bill-pending">Pending bill</dd>
          </>
        ) : (
          <>
            <dt>Supplier bill</dt>
            <dd>−{usd(result.bill)}</dd>
            <dt className="supplier-bill-strong">Profit</dt>
            <dd
              className={`supplier-bill-strong ${
                result.profit !== null && result.profit < 0
                  ? 'supplier-bill-loss'
                  : 'supplier-bill-gain'
              }`}
            >
              {result.profit === null ? '—' : usd(result.profit)}
            </dd>
            <dt>Margin</dt>
            <dd>{result.marginPct === null ? '—' : `${result.marginPct.toFixed(1)}%`}</dd>
          </>
        )}
      </dl>

      {canWrite ? (
        <div className="supplier-bill-actions">
          <Button
            variant={lines.length === 0 ? 'primary' : 'secondary'}
            onClick={() => {
              setError('');
              setDraft(draftFrom(lines));
            }}
          >
            {lines.length === 0 ? 'Add costs' : 'Edit bill'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
